import {ReactElement, useCallback, useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';

import {MemberProductScoreType, MemberProductType} from '../types';
import {RatingModalDataType} from '../contexts/RatingModalProvider.client';
import {useMemberProduct} from '../contexts/MemberProductProvider.client';

interface ProductHoverButtonsProps {
  productId: string; // Either marketplace Product.uuid or Shopify Product.id
  rating: MemberProductScoreType;
  productImage?: string;
  productTitle?: string;
  showRateButton?: boolean;
  showWatchButton?: boolean;
  onRatingSuccess?: (myMemberProduct: MemberProductType) => void;
}

export default function ProductHoverButtons({
  productId,
  productImage,
  productTitle,
  rating,
  showRateButton = true,
  showWatchButton = true,
  onRatingSuccess,
}: ProductHoverButtonsProps): ReactElement {
  const [ratingModalData, setRatingModalData] = useState<RatingModalDataType>({
    productId,
    productImage,
    productTitle,
    rating,
    onSuccess: (myMemberProduct) => onRatingSuccess?.(myMemberProduct),
  });

  const {myMemberProduct, toggleWatchProduct, openRatingModal} =
    useMemberProduct();

  useEffect(() => {
    if (myMemberProduct) {
      setRatingModalData((prevState) => ({
        ...prevState,
        rating: myMemberProduct.rating,
      }));
    }
  }, [myMemberProduct, setRatingModalData, productId]);

  const isWatched = useMemo(
    () =>
      myMemberProduct &&
      myMemberProduct?.product?.shopify_resource_id === productId &&
      myMemberProduct.is_watched,
    [myMemberProduct, productId],
  );

  const isRated = useMemo(
    () =>
      myMemberProduct &&
      myMemberProduct?.product?.shopify_resource_id === productId &&
      Number(myMemberProduct.rating) > 0,
    [myMemberProduct, productId],
  );
  const handleRatingClick = useCallback(
    () => openRatingModal(ratingModalData),
    [openRatingModal, ratingModalData],
  );

  return (
    <div className="product__hover">
      {showRateButton && (
        <span
          role="button"
          tabIndex={-1}
          onClick={handleRatingClick}
          className={classNames(
            'product__hover-btn product__hover-fav tooltip tooltip-no-icon',
            {
              active: isRated,
            },
          )}
        >
          <i className="icon-heart-empty-2" />
          <span className="tooltiptext tooltiptext-small tooltiptext-no-mobile">
            {isRated ? `Change Rating` : 'Rate product'}
          </span>
        </span>
      )}
      {showWatchButton && (
        <span
          role="button"
          tabIndex={-1}
          onClick={() =>
            toggleWatchProduct({product: productId, is_watched: !isWatched})
          }
          className={classNames(
            'product__hover-btn product__hover-buy tooltip tooltip-no-icon',
            {
              active: isWatched,
            },
          )}
        >
          <i className="icon-visibility" />
          <span className="tooltiptext tooltiptext-small tooltiptext-no-mobile">
            {isWatched ? `Unwatch` : 'Watch'} product
          </span>
        </span>
      )}
    </div>
  );
}
